/**
 * The frontend's reads of a record's external sources: the per-integration
 * detail query for a source, the cached context snapshot on a source row, and
 * which source types a set of records carries. Source writes go through core
 * (`auger.sources`, `auger.rows`).
 */

import type { ApolloClient, TypedDocumentNode } from "@apollo/client/core"
import {
  fetchAirtableOrganizationSource,
  fetchAirtablePersonSource,
  fetchHubSpotOrganizationSource,
  fetchHubSpotPersonSource,
  fetchLumaEventSource,
  fetchLumaPersonSource,
  fetchMailchimpPersonSource,
  fetchPipedriveOrganizationSource,
  fetchPipedrivePersonSource,
  fetchSanityOrganizationSource,
  fetchSanityPersonSource,
  fetchSlackPersonSource,
  fetchSourceContext as fetchSourceContextQuery,
  fetchSourcesForRecords,
  fetchStandardMetricsOrganizationSource,
} from "@/common/hasura/sources"

/** A per-integration detail query: the source row aliased as `source`. */
export type SourceDetailQuery = TypedDocumentNode<{ source?: unknown }, { id: string }>

type SourceDetailQueryMap = Partial<Record<string, Partial<Record<string, SourceDetailQuery>>>>

// Map of source type fetch queries by record type
const sourceTypeQueryMap = {
  person: {
    airtable: fetchAirtablePersonSource,
    pipedrive: fetchPipedrivePersonSource,
    mailchimp: fetchMailchimpPersonSource,
    luma: fetchLumaPersonSource,
    slack: fetchSlackPersonSource,
    hubspot: fetchHubSpotPersonSource,
    sanity: fetchSanityPersonSource,
  },
  organization: {
    airtable: fetchAirtableOrganizationSource,
    pipedrive: fetchPipedriveOrganizationSource,
    hubspot: fetchHubSpotOrganizationSource,
    sanity: fetchSanityOrganizationSource,
    standard_metrics: fetchStandardMetricsOrganizationSource,
  },
  event: {
    luma: fetchLumaEventSource,
  },
} satisfies SourceDetailQueryMap

/** The detail query for a source of `sourceType` on a `recordType` record, or
 *  undefined when that integration has none. */
export function sourceDetailQuery(
  recordType: string,
  sourceType: string
): SourceDetailQuery | undefined {
  const queries: SourceDetailQueryMap = sourceTypeQueryMap
  return queries[recordType]?.[sourceType]
}

/** Each record's source types, keyed by record id. */
export function fetchSourcesForRecordIds(
  recordIds: number[],
  client: ApolloClient<unknown>
): Promise<Map<number, string[]>> {
  if (!recordIds.length) return Promise.resolve(new Map<number, string[]>())
  return client
    .query({
      query: fetchSourcesForRecords,
      variables: { recordIds },
      fetchPolicy: "network-only",
    })
    .then(({ data }) => {
      const map = new Map<number, string[]>()
      for (const row of data?.sources || []) {
        const list = map.get(row.record_id) || []
        list.push(row.source_type)
        map.set(row.record_id, list)
      }
      return map
    })
}

/** The cached context on the newest row for a (sourceType, sourceId) pair, or
 *  null when there is none. */
export function fetchSourceContext(
  sourceType: string,
  sourceId: string | number,
  client: ApolloClient<unknown>
) {
  return client
    .query({
      query: fetchSourceContextQuery,
      variables: { sourceType, sourceId: String(sourceId) },
      fetchPolicy: "cache-first",
    })
    .then(({ data }) => data.record_sources[0]?.context ?? null)
}
